"use client";

import { useEffect, useMemo, useState } from "react";
import { extractInfoFromUrl, getStorage, setStorage } from "../../lib/util";

export const useProductPriceDisplay = (
  accessPoint = null,
  product,
  recievedPriceOption = null,
) => {
  // Resolve the "storeFrontDisplayPrice" according to rules:
  // - if accessPoint.settings.storeFrontDisplayPrice exists use it
  // - if both accessPoint's value and recievedPriceOption exist and differ,
  //   prefer recievedPriceOption (per your "notDeepEqual -> use recieved" rule)
  // - otherwise fallback to recievedPriceOption
  const resolvedStoreFrontDisplayPrice = useMemo(() => {
    const apVal = accessPoint?.settings?.storeFrontDisplayPrice ?? null;
    const recVal = recievedPriceOption ?? null;

    if (apVal) {
      // If both exist and differ, prefer the received value
      if (recVal && recVal !== apVal) return recVal;
      return apVal;
    }

    return recVal;
  }, [accessPoint, recievedPriceOption]);

  const [priceDisplayMode, setPriceDisplayMode] = useState(() => {
    const saved = getStorage("priceDisplayMode");
    if (saved) return saved;

    if (resolvedStoreFrontDisplayPrice) {
      switch (resolvedStoreFrontDisplayPrice) {
        case "DISCOUNT":
          return "d";
        case "PROMO":
          return "p";
        case "MARKUP":
          return "m";
        case "NORMAL":
          return "s";
        default:
          return null;
      }
    }

    return null;
  });

  useEffect(() => {
    // On mount and whenever the URL / storage / resolved store price changes,
    // re-evaluate the effective mode.
    let urlMode = extractInfoFromUrl("p");
    const storedMode = getStorage("priceDisplayMode");
    let mode;

    if (urlMode) {
      if (urlMode.length > 1) {
        urlMode = urlMode.charAt(0);
      }
      mode = urlMode;
      setStorage("priceDisplayMode", urlMode);
    } else if (storedMode) {
      mode = storedMode;
    } else if (resolvedStoreFrontDisplayPrice) {
      // derive from resolved storeFrontDisplayPrice when there's no URL or stored override
      switch (resolvedStoreFrontDisplayPrice) {
        case "DISCOUNT":
          mode = "d";
          break;
        case "PROMO":
          mode = "p";
          break;
        case "MARKUP":
          mode = "m";
          break;
        case "NORMAL":
          mode = "s";
          break;
        default:
          mode = null;
      }
    } else {
      mode = null;
    }

    setPriceDisplayMode(mode);
  }, [resolvedStoreFrontDisplayPrice]);

  const getDisplayPrice = (product) => {
    switch (priceDisplayMode) {
      case "d":
        return product?.discountPrice ?? product?.amount;
      case "p":
        return product?.promoPrice ?? product?.amount;
      case "m":
        return product?.markUpPrice ?? product?.amount;
      case "s":
        return product?.amount;
      default:
        return product?.amount;
    }
  };

  const productPrice = getDisplayPrice(product);

  const selectedPricePayload = {
    usePromo: priceDisplayMode === "p",
    useDiscount: priceDisplayMode === "d",
    useMarkup: priceDisplayMode === "m",
  };

  return {
    getDisplayPrice,
    productPrice,
    selectedPricePayload,
    setPriceDisplayMode,
    priceDisplayMode,
  };
};
